/**
 * BundleCartValidator - Enforces strict bundle integrity in Shopify Cart & Drawer
 * 
 * Rules enforced:
 * 1. If an add-on item exists with `_bundle_role === 'addon'` / `_bundle_parent_id`, its matching main product MUST be in the cart.
 * 2. If the main product is removed from cart, the add-on discount is removed / add-on is reconciled automatically.
 * 3. Add-on quantity cannot exceed the allowed quantity of its parent main product.
 * 4. Multiple main products and their add-ons are tracked independently via unique `_bundle_group_id`.
 */

class BundleCartValidator {
  static #instance = null;
  #isValidating = false;
  #lastCartJson = '';

  constructor() {
    if (BundleCartValidator.#instance) return BundleCartValidator.#instance;
    BundleCartValidator.#instance = this;

    this.#init();
  }

  static init() {
    if (!window.bundleCartValidator) {
      window.bundleCartValidator = new BundleCartValidator();
    }
  }

  #init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.validate());
    } else {
      this.validate();
    }

    // Listen to Theme Cart Events
    document.addEventListener('cart:update', () => this.validate());
    document.addEventListener('cart:add', () => this.validate());

    // Listen to Cart Drawer opening
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        const dialog = cartDrawer.querySelector('dialog');
        if (dialog && dialog.hasAttribute('open')) {
          this.validate();
        }
      });
      observer.observe(cartDrawer, { attributes: true, subtree: true, attributeFilter: ['open'] });
    }

    // Intercept standard fetch requests to /cart/change and /cart/clear
    this.#interceptCartRequests();
  }

  #interceptCartRequests() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      if (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear')) {
        setTimeout(() => {
          self.validate();
        }, 150);
      }

      return response;
    };
  }

  /**
   * Main Cart Validation & Enforcement Routine
   */
  async validate() {
    if (this.#isValidating) return;
    this.#isValidating = true;

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();

      const cartJson = JSON.stringify(cart.items.map((i) => ({ id: i.id, q: i.quantity, p: i.properties })));
      if (cartJson === this.#lastCartJson) {
        this.#isValidating = false;
        return;
      }
      this.#lastCartJson = cartJson;

      if (!cart.items || cart.items.length === 0) {
        this.#isValidating = false;
        return;
      }

      // 1. Group items by bundle properties
      const mainProducts = new Map(); // group_id -> mainItem
      const addonItems = []; // list of addon items

      cart.items.forEach((item, index) => {
        const props = item.properties || {};
        const role = props._bundle_role;
        const groupId = props._bundle_group_id;
        const parentId = props._bundle_parent_id;

        if (role === 'main' || (props._bundle_group_id && !props._bundle_parent_id)) {
          if (groupId) {
            mainProducts.set(groupId, { ...item, lineIndex: index + 1 });
          }
          // Also index by product id
          mainProducts.set(`prod_${item.product_id}`, { ...item, lineIndex: index + 1 });
        } else if (role === 'addon' || parentId) {
          addonItems.push({ ...item, lineIndex: index + 1, props, groupId, parentId });
        }
      });

      if (addonItems.length === 0) {
        this.#isValidating = false;
        return;
      }

      // 2. Check each add-on for valid parent and quantity
      let needsReconciliation = false;
      const updates = {}; // { line_key or variant_id: new_quantity }

      for (const addon of addonItems) {
        const matchingMainByGroup = addon.groupId ? mainProducts.get(addon.groupId) : null;
        const matchingMainByParentId = addon.parentId ? mainProducts.get(`prod_${addon.parentId}`) : null;
        const parentMain = matchingMainByGroup || matchingMainByParentId;

        // Condition A: Parent product is completely missing from cart
        if (!parentMain) {
          console.warn(`[Bundle Validator] Add-on "${addon.title}" removed because parent main product was removed.`);
          updates[addon.key || addon.lineIndex] = 0;
          needsReconciliation = true;
        }
        // Condition B: Add-on quantity exceeds parent quantity
        else if (addon.quantity > parentMain.quantity) {
          console.warn(`[Bundle Validator] Add-on "${addon.title}" quantity reduced to match parent quantity (${parentMain.quantity}).`);
          updates[addon.key || addon.lineIndex] = parentMain.quantity;
          needsReconciliation = true;
        }
      }

      // 3. Execute Cart Reconciliation if violation found
      if (needsReconciliation) {
        // Query section IDs to refresh drawer and components
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = new Set();
        cartItemsComponents.forEach((c) => {
          if (c.dataset.sectionId) sectionIds.add(c.dataset.sectionId);
        });

        const drawerSection = document.querySelector('cart-drawer-component')?.closest('.shopify-section');
        if (drawerSection?.id) {
          sectionIds.add(drawerSection.id.replace('shopify-section-', ''));
        }

        const updatePayload = {
          updates: updates,
        };

        if (sectionIds.size > 0) {
          updatePayload.sections = Array.from(sectionIds).join(',');
        }

        const updateRes = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(updatePayload),
        });

        const updatedCart = await updateRes.json();
        this.#lastCartJson = JSON.stringify((updatedCart.items || []).map((i) => ({ id: i.id, q: i.quantity, p: i.properties })));

        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart: updatedCart } }));
      }
    } catch (err) {
      console.error('[Bundle Validator] Error during validation:', err);
    } finally {
      this.#isValidating = false;
    }
  }
}

// Auto-initialize bundle validator
BundleCartValidator.init();
